import React from 'react';
import { TrendingUp, Flame, ArrowUp, BarChart3, Activity, Clock, Zap, Hash } from 'lucide-react';
import type { Post } from '../types';
import { usePosts } from '../hooks/usePosts';
import { useMediaQuery } from '../hooks/useMediaQuery';

interface TrendCategoryProps {
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  count?: number;
  onClick: () => void;
}

function TrendCategory({ icon, label, isActive, count, onClick }: TrendCategoryProps) {
  return (
    <button
      onClick={onClick}
      data-active={isActive}
      className={`flex items-center gap-2 px-3 py-2 rounded-full transition-colors min-w-[100px] snap-center ${
        isActive 
          ? 'bg-blue-600 text-white' 
          : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700/70'
      }`}
    >
      {icon}
      <span className="text-sm font-medium whitespace-nowrap">{label}</span>
      {count && (
        <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-blue-500/30 text-white' : 'bg-gray-700/50 text-gray-300'}`}>
          {count}
        </span>
      )}
    </button>
  );
}

interface TrendingVideoProps {
  post: Post;
  rank?: number;
  isRising?: boolean;
  views?: string;
  onClick: () => void;
}

function TrendingVideo({ post, rank, isRising, views = '1.2M', onClick }: TrendingVideoProps) {
  return (
    <div 
      onClick={onClick}
      className="bg-gray-900/50 rounded-lg overflow-hidden hover:bg-gray-800/50 transition-colors cursor-pointer"
    >
      <div className="relative aspect-video bg-gray-800">
        <img 
          src={post.videoUrl.replace('.mp4', '.jpg')} 
          alt={post.caption}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.src = "https://images.unsplash.com/photo-1557682250-33bd709cbe85?w=600";
          }}
        />
        {rank && (
          <div className="absolute top-2 left-2 bg-black/70 rounded-md px-2 py-1 text-sm font-bold">
            #{rank}
          </div>
        )}
        {isRising && (
          <div className="absolute top-2 right-2 bg-green-600/90 rounded-md px-2 py-1 flex items-center gap-1">
            <ArrowUp className="w-3 h-3" />
            <span className="text-xs font-medium">Rising</span>
          </div>
        )}
        <div className="absolute bottom-2 right-2 bg-black/70 rounded-md px-2 py-1 text-xs">
          {views} views
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
          <div className="p-3 bg-blue-600/90 rounded-full">
            <Flame className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>
      <div className="p-3">
        <div className="flex items-start gap-2">
          <img 
            src={post.userAvatar} 
            alt={post.username}
            className="w-8 h-8 rounded-full shrink-0 mt-1"
          />
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium line-clamp-2">{post.caption}</p>
            <p className="text-gray-400 text-xs mt-1">@{post.username}</p>
            <div className="flex flex-wrap gap-1 mt-2">
              {post.hashtags.slice(0, 3).map(tag => (
                <span key={tag} className="text-blue-400 text-xs">#{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: string;
  change: string;
  icon: React.ReactNode;
  isPositive?: boolean;
}

function StatCard({ title, value, change, icon, isPositive = true }: StatCardProps) {
  return (
    <div className="bg-gray-900/50 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-400 text-sm">{title}</span>
        <div className={`p-2 rounded-md ${isPositive ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
          {icon}
        </div>
      </div>
      <p className="text-white text-2xl font-bold">{value}</p>
      <div className={`flex items-center mt-1 ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
        <ArrowUp className={`w-4 h-4 mr-1 ${!isPositive && 'transform rotate-180'}`} />
        <span className="text-sm">{change}</span>
      </div>
    </div>
  );
}

export function TrendsView({ onPostSelect }: { onPostSelect: (post: Post) => void }) {
  const [activeCategory, setActiveCategory] = React.useState('now');
  const { posts } = usePosts();
  const categoriesRef = React.useRef<HTMLDivElement>(null);
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  
  // Create a different set of posts for each category
  const allPosts = React.useMemo(() => {
    return {
      now: posts.map(post => ({...post, id: post.id + '_now'})),
      daily: posts.map(post => ({...post, id: post.id + '_daily'})),
      weekly: posts.map(post => ({...post, id: post.id + '_weekly'})),
      monthly: posts.map(post => ({...post, id: post.id + '_monthly'}))
    };
  }, [posts]);
  
  // Get the posts for the current active category
  const currentPosts = allPosts[activeCategory as keyof typeof allPosts] || allPosts.now;
  
  React.useEffect(() => {
    // Scroll active category into view when changed
    if (categoriesRef.current && !isDesktop) {
      const activeButton = categoriesRef.current.querySelector('[data-active="true"]');
      if (activeButton) {
        activeButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [activeCategory, isDesktop]);

  return (
    <div className={`fixed inset-0 bg-black overflow-y-auto ${isDesktop ? 'pt-16 ml-[240px]' : 'pt-16 pb-16'} safe-area-top safe-area-bottom overscroll-y-contain`}>
      <div className="px-4 py-3 bg-black sticky top-16 z-10 border-b border-gray-800">
        <div className="flex items-center mb-4">
          <Flame className="w-6 h-6 text-blue-500 mr-2" />
          <h1 className="text-white text-xl font-bold">Trending</h1>
        </div>
        
        <div 
          ref={categoriesRef}
          className={`${isDesktop ? 'flex flex-wrap' : 'overflow-x-auto flex'} gap-2 pb-2 -mx-4 px-4 scrollbar-hide snap-x-mandatory touch-pan-x`}
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <TrendCategory 
            icon={<Zap className="w-4 h-4" />} 
            label="Right Now" 
            isActive={activeCategory === 'now'} 
            onClick={() => setActiveCategory('now')} 
          />
          <TrendCategory 
            icon={<Clock className="w-4 h-4" />} 
            label="Today" 
            isActive={activeCategory === 'daily'} 
            onClick={() => setActiveCategory('daily')} 
            count={24}
          />
          <TrendCategory 
            icon={<Activity className="w-4 h-4" />} 
            label="This Week" 
            isActive={activeCategory === 'weekly'} 
            onClick={() => setActiveCategory('weekly')} 
            count={7}
          />
          <TrendCategory 
            icon={<BarChart3 className="w-4 h-4" />} 
            label="This Month" 
            isActive={activeCategory === 'monthly'} 
            onClick={() => setActiveCategory('monthly')} 
          />
        </div>
      </div>
      
      <div className="p-4 max-w-screen-xl mx-auto">
        {/* Stats Overview Section */}
        <div className="mb-6">
          <h2 className="text-white font-bold text-lg mb-3">Trending Overview</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard 
              title="Total Views" 
              value="12.5M" 
              change="+24% from yesterday"
              icon={<BarChart3 className="w-5 h-5 text-green-400" />}
            />
            <StatCard 
              title="Active Users" 
              value="845K" 
              change="+15% from yesterday"
              icon={<Activity className="w-5 h-5 text-green-400" />}
            />
            <StatCard 
              title="Avg Watch Time" 
              value="2:45"
              change="-5% from yesterday"
              icon={<Clock className="w-5 h-5 text-red-400" />}
              isPositive={false}
            />
            <StatCard 
              title="Viral Ratio" 
              value="8.7%"
              change="+3.2% from yesterday"
              icon={<TrendingUp className="w-5 h-5 text-green-400" />}
            />
          </div>
        </div>
        
        {/* Top Rising Section */}
        <div className="mb-6">
          <h2 className="text-white font-bold text-lg mb-3 flex items-center">
            <TrendingUp className="w-5 h-5 text-green-500 mr-2" />
            Top Rising
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentPosts.slice(0, 3).map((post, index) => (
              <TrendingVideo 
                key={post.id} 
                post={post} 
                rank={index + 1} 
                isRising={true}
                views={`${(Math.random() * 5 + 1).toFixed(1)}M`}
                onClick={() => onPostSelect(post)} 
              />
            ))}
          </div>
        </div>
        
        {/* Top Viral Section */}
        <div className="mb-6">
          <h2 className="text-white font-bold text-lg mb-3 flex items-center">
            <Flame className="w-5 h-5 text-orange-500 mr-2" />
            Most Viral
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {currentPosts.slice(0, 2).map((post, index) => (
              <div
                key={post.id + '_viral'}
                onClick={() => onPostSelect(post)}
                className="bg-gray-900/50 rounded-lg overflow-hidden hover:bg-gray-800/50 transition-colors cursor-pointer relative"
              >
                <div className="absolute top-0 left-0 w-full h-full z-10 bg-gradient-to-t from-black via-transparent to-transparent pointer-events-none"></div>
                <div className="relative aspect-[4/3]">
                  <img 
                    src={post.videoUrl.replace('.mp4', '.jpg')} 
                    alt={post.caption}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src = "https://images.unsplash.com/photo-1557682250-33bd709cbe85?w=600";
                    }}
                  />
                  <div className="absolute top-3 left-3 z-20 flex items-center gap-2">
                    <div className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-md">VIRAL</div>
                    <div className="bg-black/70 text-white text-xs px-2 py-1 rounded-md flex items-center gap-1">
                      <Flame className="w-3 h-3 text-orange-400" />
                      {(Math.random() * 500 + 500).toFixed(0)}x
                    </div>
                  </div>
                  <div className="absolute bottom-3 right-3 z-20 bg-black/70 text-white text-xs px-2 py-1 rounded-md">
                    {(Math.random() * 10 + 2).toFixed(1)}M views
                  </div>
                </div>
                <div className="p-4 relative z-20">
                  <p className="text-white text-sm font-medium line-clamp-2">{post.caption}</p>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-2">
                      <img 
                        src={post.userAvatar} 
                        alt={post.username}
                        className="w-6 h-6 rounded-full"
                      />
                      <span className="text-gray-400 text-xs">@{post.username}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Hash className="w-3 h-3 text-blue-400" />
                      <span className="text-blue-400 text-xs">{post.hashtags[0]}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Trending by Category */}
        <div className="mb-6">
          <h2 className="text-white font-bold text-lg mb-3">Trending by Category</h2>
          
          {/* Politics */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-white font-medium flex items-center">
                <span className="bg-blue-600/20 text-blue-400 text-xs px-2 py-0.5 rounded mr-2">Politics</span>
                Top Videos
              </h3>
              <button className="text-blue-400 text-xs">View All</button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
              {currentPosts.slice(0, 5).map((post) => (
                <div 
                  key={post.id + '_politics'}
                  onClick={() => onPostSelect(post)}
                  className="bg-gray-900/50 rounded-lg overflow-hidden hover:bg-gray-800/50 transition-colors cursor-pointer"
                >
                  <div className="relative aspect-square">
                    <img 
                      src={post.videoUrl.replace('.mp4', '.jpg')} 
                      alt={post.caption}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = "https://images.unsplash.com/photo-1557682250-33bd709cbe85?w=300";
                      }}
                    />
                    <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                      {(Math.random() * 1.5 + 0.5).toFixed(1)}M
                    </div>
                  </div>
                  <div className="p-2">
                    <p className="text-white text-xs line-clamp-1">{post.caption}</p>
                    <p className="text-gray-400 text-xs mt-1">@{post.username}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Technology */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-white font-medium flex items-center">
                <span className="bg-green-600/20 text-green-400 text-xs px-2 py-0.5 rounded mr-2">Technology</span>
                Top Videos
              </h3>
              <button className="text-blue-400 text-xs">View All</button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
              {currentPosts.slice(0, 5).map((post) => (
                <div 
                  key={post.id + '_tech'}
                  onClick={() => onPostSelect(post)}
                  className="bg-gray-900/50 rounded-lg overflow-hidden hover:bg-gray-800/50 transition-colors cursor-pointer"
                >
                  <div className="relative aspect-square">
                    <img 
                      src={post.videoUrl.replace('.mp4', '.jpg')} 
                      alt={post.caption}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = "https://images.unsplash.com/photo-1557682250-33bd709cbe85?w=300";
                      }}
                    />
                    <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                      {(Math.random() * 1.5 + 0.5).toFixed(1)}M
                    </div>
                  </div>
                  <div className="p-2">
                    <p className="text-white text-xs line-clamp-1">{post.caption}</p>
                    <p className="text-gray-400 text-xs mt-1">@{post.username}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}